import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Select, 
  Button, 
  Space,
  message,
} from 'antd';
import { SaveOutlined, PlayCircleOutlined } from '@ant-design/icons';
import AdRuleAPIService from '../../services/AdRuleAPIService';
import WorkflowAPIService from '../../services/WorkflowAPIService';

const { TextArea } = Input;
const { Option } = Select;

/**
 * RuleForm - Modal form để tạo/chỉnh sửa Rule
 * 
 * Được sử dụng từ:
 * - AdsOptimization/index.js: Edit rule từ RuleList
 * 
 * Khi tạo rule mới: Tự động tạo workflow rỗng gắn với rule
 */
const RuleForm = ({ 
  visible, 
  onCancel, 
  onSave, 
  editingRule = null,
  onEditFlow
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      if (editingRule) {
        form.setFieldsValue({
          name: editingRule.name,
          description: editingRule.description,
          type: editingRule.type,
          platform: editingRule.platform,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          platform: 'google_ads',
          type: 'budget_optimization'
        });
      }
    }
  }, [visible, editingRule, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Rule data with default values for hidden fields
      const ruleData = {
        ...values,
        priority: editingRule?.priority || 1,
        frequency: editingRule?.frequency || 'daily',
        conditions: editingRule?.conditions || {},
        actions: editingRule?.actions || {}
      };

      setLoading(true);
      
      let response;
      if (editingRule?.id) {
        // Update existing rule - don't create new workflow
        response = await AdRuleAPIService.updateRule(editingRule.id, ruleData);
        
        if (response.success) {
          message.success('Cập nhật rule thành công');
          onSave?.(response.data);
          onCancel();
        } else {
          throw new Error(response.message);
        }
      } else {
        // Create new rule with new workflow
        // Step 1: Create empty workflow first
        const workflowData = {
          name: `Workflow - ${values.name}`,
          description: values.description || `Workflow for rule: ${values.name}`,
          type: 'ads_optimization',
          status: 'draft',
          nodes: [],
          edges: [],
          variables: {},
          metadata: {
            createdAt: new Date().toISOString(),
            nodeCount: 0,
            edgeCount: 0
          }
        };
        
        const workflowResponse = await WorkflowAPIService.createWorkflow(workflowData);
        
        if (!workflowResponse.success) {
          throw new Error(workflowResponse.message || 'Failed to create workflow');
        }
        
        // Step 2: Create rule with workflowId
        const ruleWithWorkflow = {
          ...ruleData,
          workflowId: workflowResponse.data.id
        };
        
        response = await AdRuleAPIService.createRule(ruleWithWorkflow);
        
        if (response.success) {
          message.success('Tạo rule và workflow thành công');
          onSave?.(response.data);
          onCancel();
        } else {
          // Rollback: delete created workflow if rule creation fails
          await WorkflowAPIService.deleteWorkflow(workflowResponse.data.id);
          throw new Error(response.message);
        }
      }
    } catch (error) {
      console.error('Error saving rule:', error);
      message.error('Lỗi khi lưu rule: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditFlow = () => {
    form.validateFields().then(values => {
      const ruleData = {
        ...editingRule,
        ...values,
        conditions: editingRule?.conditions || {},
        actions: editingRule?.actions || {}
      };
      
      onEditFlow?.(ruleData);
      onCancel();
    }).catch(() => {
      message.error('Vui lòng điền đầy đủ các trường bắt buộc');
    });
  };

  return (
    <Modal
      title={editingRule ? '✏️ Chỉnh sửa Rule' : '➕ Tạo Rule mới'}
      open={visible}
      onCancel={onCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Hủy
        </Button>,
        editingRule?.id && !editingRule.id.startsWith('new_') && (
          <Button 
            key="flow" 
            icon={<PlayCircleOutlined />}
            onClick={handleEditFlow}
          >
            Chỉnh sửa Workflow
          </Button>
        ),
        <Button 
          key="save" 
          type="primary" 
          icon={<SaveOutlined />}
          loading={loading}
          onClick={handleSave}
        >
          {editingRule ? 'Lưu' : 'Tạo Rule'}
        </Button>
      ].filter(Boolean)}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          name="name"
          label="Tên Rule"
          rules={[{ required: true, message: 'Vui lòng nhập tên rule' }]}
        >
          <Input placeholder="VD: Tối ưu chiến dịch CTR cao" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Mô tả"
        >
          <TextArea 
            rows={2}
            placeholder="Mô tả chi tiết về rule này..."
          />
        </Form.Item>

        <div style={{ display: 'flex', gap: 16 }}>
          <Form.Item
            name="type"
            label="Loại Rule"
            rules={[{ required: true, message: 'Vui lòng chọn loại rule' }]}
            style={{ flex: 1 }}
          >
            <Select placeholder="Chọn loại rule">
              {AdRuleAPIService.getRuleTypes().map(type => (
                <Option key={type.value} value={type.value}>{type.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="platform"
            label="Platform"
            rules={[{ required: true, message: 'Vui lòng chọn platform' }]}
            style={{ flex: 1 }}
          >
            <Select placeholder="Chọn platform">
              {AdRuleAPIService.getPlatforms().map(platform => (
                <Option key={platform.value} value={platform.value}>{platform.label}</Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default RuleForm;